//@ts-ignore
import { IonCard, IonCardContent, IonCol, IonGrid, IonItem, IonLoading, IonRow, IonText } from "@ionic/react";
import { createContext, FC, ReactNode, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import CustomToast from "../components/CustomToast/CustomToast";
import { Paciente, SesionAgenda, Turno, Usuario } from "../interfaces";
import LoginPopUp from "../pages/Login/LoginPopUp";
import useNotificacion from "./notificacion";

interface SessionData {
  agendaCargada: SesionAgenda | null;
  pacienteCargado: Paciente | null;
  hcUL: Usuario | null;
  turnoSeleccionado: Turno | null;
  sesionInvalida: boolean;
  cargandoSession: boolean;
}

interface SessionContextType extends SessionData {
  setAgendaCargada: (agendaCargada: SesionAgenda | null) => void;
  setPacienteCargado: (pacienteCargado: Paciente | null) => void;
  setHcUL: (hcUL: Usuario | null) => void;
  setTurnoSeleccionado: (turnoSeleccionado: Turno | null) => void;
  setCargandoSession: (cargando: boolean) => void;
  setSesionInvalida: (invalida: boolean) => void;
  mostrarNotificacion: (mostrarMensaje: boolean, mensajeMostrar: string, colorMostrar: string) => void;
}

interface SessionProviderProps {
  children: ReactNode;
}

const SessionContext = createContext<SessionContextType>({
  agendaCargada: null,
  pacienteCargado: null,
  hcUL: null,
  turnoSeleccionado: null,
  cargandoSession: false,
  sesionInvalida: true,
  setAgendaCargada: () => {},
  setPacienteCargado: () => {},
  setHcUL: () => {},
  setTurnoSeleccionado: () => {},
  setCargandoSession: () => {},
  setSesionInvalida: () => {},
  mostrarNotificacion: () => {},
});

//* Hook personalizado para acceder al contexto de sesión
export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession debe ser usado dentro de un SessionProvider");
  }
  return context;
};

//* Proveedor de sesión que contiene el estado y funciones para modificarlo
//@ts-ignore
export const SessionProvider: FC<SessionProviderProps> = ({ children }) => {
  const location = useLocation();
  const [agendaCargada, setAgendaCargada] = useState<SesionAgenda | null>(null);
  const [pacienteCargado, setPacienteCargado] = useState<Paciente | null>(null);
  const [hcUL, setHcUL] = useState<Usuario | null>(null);
  const [turnoSeleccionado, setTurnoSeleccionado] = useState<Turno | null>(null);
  const [cargandoSession, setCargandoSession] = useState<boolean>(false);
  const [sesionInvalida, setSesionInvalida] = useState<boolean>(true);

  const { mostrar, mensaje, color, mostrarNotificacion } = useNotificacion();

  useEffect(() => {
    const agendaCargadaFromStorage = sessionStorage.getItem("AgendaCargada");
    const pacienteCargadoFromStorage = sessionStorage.getItem("PacienteCargado");
    const hcULFromStorage = sessionStorage.getItem("hcUL");
    const turnoSeleccionadoFromStorage = sessionStorage.getItem("turnoSeleccionado");

    //* Verificar y parsear los datos del sessionStorage
    if (agendaCargadaFromStorage) {
      setAgendaCargada(JSON.parse(agendaCargadaFromStorage));
    }
    if (pacienteCargadoFromStorage) {
      setPacienteCargado(JSON.parse(pacienteCargadoFromStorage));
    }
    if (hcULFromStorage) {
      setHcUL(JSON.parse(hcULFromStorage));
    }
    if (turnoSeleccionadoFromStorage) {
      setTurnoSeleccionado(JSON.parse(turnoSeleccionadoFromStorage));
    }

    //* Establecer cargandoSession en falso después de que se hayan cargado los datos
    setCargandoSession(false);
  }, []);

  // El tema se gestiona por CSS con prefers-color-scheme; no hay toggle manual

  //* Funciones para actualizar el estado y almacenar en el sessionStorage
  const updateAgendaCargada = (agendaCargada: SesionAgenda | null) => {
    setAgendaCargada(agendaCargada);
    sessionStorage.setItem("AgendaCargada", JSON.stringify(agendaCargada));
  };

  const updatePacienteCargado = (pacienteCargado: Paciente | null) => {
    setPacienteCargado(pacienteCargado);
    sessionStorage.setItem("PacienteCargado", JSON.stringify(pacienteCargado));
  };

  const updateHcUL = (hcUL: Usuario | null) => {
    setHcUL(hcUL);
    sessionStorage.setItem("hcUL", JSON.stringify(hcUL));
  };

  const updateTurnoSeleccionado = (turnoSeleccionado: Turno | null) => {
    setTurnoSeleccionado(turnoSeleccionado);
    sessionStorage.setItem("turnoSeleccionado", JSON.stringify(turnoSeleccionado));
  };

  // const isAuthenticated = (): boolean => {
  //   let hcULSS = sessionStorage.getItem("hcUL");
  //   if (hcULSS) {
  //     hcULSS = JSON.parse(hcULSS);
  //     if (typeof hcULSS !== "string" && hcULSS !== null) {
  //       if (hcULSS === hcUL) {
  //         return dayjs(hcULSS.fechaSesion).diff(dayjs(), "minute", true) < 30;
  //       } else return false;
  //     } else return false;
  //   } else return false;
  // };

  useEffect(() => {
    if (hcUL === null) {
      updateAgendaCargada(null);
      updatePacienteCargado(null);
      updateTurnoSeleccionado(null);
    }
  }, [hcUL]);

  const contextValue: SessionContextType = {
    agendaCargada,
    pacienteCargado,
    hcUL,
    turnoSeleccionado,
    cargandoSession,
    sesionInvalida,
    setAgendaCargada: updateAgendaCargada,
    setPacienteCargado: updatePacienteCargado,
    setHcUL: updateHcUL,
    setTurnoSeleccionado: updateTurnoSeleccionado,
    setCargandoSession,
    setSesionInvalida,
    mostrarNotificacion,
  };

  const renderCargando = () => {
    return (
      <IonGrid>
        <IonRow className="LoginViewRow justify-content-center">
          <IonCol>
            <IonCard>
              <IonCardContent>
                <IonItem>
                  <IonText color="danger">
                    <IonLoading className="custom-loading" isOpen message="Cargando sesion existente" />
                  </IonText>
                </IonItem>
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>
      </IonGrid>
    );
  };

  const renderNotificacion = () => {
    return <CustomToast color={color} mensaje={mensaje} mostrar={mostrar} />;
  };

  const renderRelogin = () => {
    return <LoginPopUp abrirModal={sesionInvalida} setAbrirModal={setSesionInvalida} />;
  };

  return (
    <SessionContext.Provider value={contextValue}>
      {cargandoSession ? renderCargando() : children}
      {sesionInvalida && location.pathname !== "/privacidad" && location.pathname !== "/Login" && renderRelogin()}
      {renderNotificacion()}
    </SessionContext.Provider>
  );
};
